import React, { useState, useRef, useEffect } from 'react';
import { Input, Button, Spin } from 'antd';
import {
  SendOutlined,
  CloseOutlined,
  DownloadOutlined,
  LikeOutlined,
  LikeFilled,
  DislikeOutlined,
  DislikeFilled,
} from '@ant-design/icons';
import { downloadCSV } from 'helpers/downloadCsv';
import { Title, Text } from 'components/Base/Typography';
import { useAppSelector } from 'redux/store';
import {
  ChatContainer,
  MessagesContainer,
  MessageWrapper,
  MessageBubble,
  AIAvatar,
  InputContainer,
  ChatHeader,
  FeedbackRow,
  CommentBox,
} from './ChatBot.styles';
import { ChartDisplay, ChartConfig } from './ChartDisplay';

interface Message {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  data?: Record<string, any>[];
  chartConfig?: ChartConfig;
  options?: string[];
  responseId?: string;
}

type Rating = 'up' | 'down' | 'unrated';
const FEEDBACK_COMMENT_MAX = 500;

const CHATBOT_API_URL =
  process.env.REACT_APP_CHATBOT_API_URL || 'https://mcp.develop.growidaho.app';
const CHATBOT_API_TOKEN = process.env.REACT_APP_CHATBOT_API_TOKEN || '';

const GENERIC_ERROR_MESSAGE =
  'Sorry, I encountered an error. Please try again.';

// Fallback text for cases the API cannot speak to itself (no JSON body sent).
// The API's own messages take precedence and are rendered verbatim; this map
// only covers the gaps: proxy/gateway timeouts, auth failures, and no response.
const messageForStatus = (status: number | null): string => {
  if (status === 502 || status === 503 || status === 504) {
    return 'That question took longer than the server allows. Try narrowing it — a single school year, or fewer columns — and ask again.';
  }
  if (status === 401 || status === 403) {
    return 'Your session could not be verified. Please refresh the page and sign in again.';
  }
  return GENERIC_ERROR_MESSAGE;
};

class ChatRequestError extends Error {
  status: number;

  userMessage?: string;

  constructor(status: number, userMessage?: string) {
    super(userMessage ?? `Chat request failed with status ${status}`);
    this.name = 'ChatRequestError';
    this.status = status;
    this.userMessage = userMessage;
  }
}

interface ChatBotProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ChatBot: React.FC<ChatBotProps> = ({ isOpen, onClose }) => {
  const { user } = useAppSelector(state => state.userSlice);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'initial',
      type: 'bot',
      content:
        'Hello! How can I help?\n\nExample questions:\n→ Do I have any overdue tasks?\n→ Which open tasks assigned to me?\n→ How many workshops do I have in November?',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [ratings, setRatings] = useState<Record<string, 'up' | 'down'>>({});
  const [commentDrafts, setCommentDrafts] = useState<Record<string, string>>(
    {},
  );
  const [commentSubmitted, setCommentSubmitted] = useState<
    Record<string, boolean>
  >({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
    },
    [],
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (overrideText?: string) => {
    const messageText = (overrideText ?? inputValue).trim();
    if (!messageText || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await fetch(`${CHATBOT_API_URL}/chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${CHATBOT_API_TOKEN}`,
          ...(user?.userId && { 'X-Grow-User-Id': user.userId }),
          ...(user?.email && { 'X-Grow-User-Email': user.email }),
        },
        body: JSON.stringify({
          message: messageText,
          ...(sessionId && { sessionId }),
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        // Prefer the API's own text — it knows what actually went wrong.
        // Nginx 504s have no JSON body, so this yields null and we fall back.
        const body = await response.json().catch(() => null);
        const apiMessage =
          typeof body?.message === 'string' ? body.message : undefined;
        throw new ChatRequestError(response.status, apiMessage);
      }

      const data = await response.json();

      // Update session ID if provided
      if (data.sessionId && data.sessionId !== sessionId) {
        setSessionId(data.sessionId);
      }

      // Create bot response message
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        content: data.message || 'I received your message.',
        timestamp: new Date(),
        data:
          Array.isArray(data.data) && data.data.length > 0
            ? data.data
            : undefined,
        chartConfig: data.chartConfig,
        options:
          Array.isArray(data.options) && data.options.length > 0
            ? data.options
            : undefined,
        responseId:
          typeof data.responseId === 'string' ? data.responseId : undefined,
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      if ((error as any)?.name === 'AbortError') return;
      const status = error instanceof ChatRequestError ? error.status : null;
      const errorContent =
        error instanceof ChatRequestError && error.userMessage
          ? error.userMessage
          : messageForStatus(status);
      // Log status + sessionId on every failure path so a user screenshot can
      // be tied back to a server log line (see GROW-4346 diagnosis).
      console.error('Chatbot request failed', {
        status,
        sessionId,
        error,
      });
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        content: errorContent,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
  };

  const sendFeedback = (
    responseId: string,
    rating: Rating,
    comment?: string,
  ) => {
    const body: Record<string, unknown> = { responseId, rating };
    if (sessionId) body.sessionId = sessionId;
    if (comment) body.comment = comment;

    fetch(`${CHATBOT_API_URL}/chat/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${CHATBOT_API_TOKEN}`,
        ...(user?.userId && { 'X-Grow-User-Id': user.userId }),
        ...(user?.email && { 'X-Grow-User-Email': user.email }),
      },
      body: JSON.stringify(body),
    }).catch(error => {
      console.error('Error sending feedback:', error);
    });
  };

  const omitKey = <T,>(source: Record<string, T>, key: string) => {
    const next = { ...source };
    delete next[key];
    return next;
  };

  const handleRate = (responseId: string, next: 'up' | 'down') => {
    const current = ratings[responseId];
    if (current === next) {
      setRatings(prev => omitKey(prev, responseId));
      setCommentDrafts(prev => omitKey(prev, responseId));
      setCommentSubmitted(prev => omitKey(prev, responseId));
      sendFeedback(responseId, 'unrated');
      return;
    }
    setRatings(prev => ({ ...prev, [responseId]: next }));
    if (next === 'up') {
      setCommentDrafts(prev => omitKey(prev, responseId));
      setCommentSubmitted(prev => omitKey(prev, responseId));
    }
    sendFeedback(responseId, next);
  };

  const handleSubmitComment = (responseId: string) => {
    const comment = (commentDrafts[responseId] ?? '').trim();
    if (!comment) return;
    sendFeedback(responseId, 'down', comment);
    setCommentDrafts(prev => omitKey(prev, responseId));
    setCommentSubmitted(prev => ({ ...prev, [responseId]: true }));
  };

  const handleDownloadCsv = (records: Record<string, any>[]) => {
    const headers = Object.keys(records[0]);
    downloadCSV(
      headers,
      records,
      record => headers.map(h => record[h] ?? ''),
      `ai-report-${new Date().toISOString().split('T')[0]}.csv`,
    );
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <ChatContainer>
      <ChatHeader>
        <div style={{ flex: 1 }}>
          <Title
            level={3}
            style={{
              textAlign: 'center',
              color: 'white',
              margin: 0,
              lineHeight: 1,
            }}
          >
            Chat with AI
          </Title>
        </div>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={onClose}
          style={{ color: 'white', position: 'absolute', right: '16px' }}
        />
      </ChatHeader>

      <MessagesContainer>
        {messages.map(message => (
          <MessageWrapper key={message.id} type={message.type}>
            {message.type === 'bot' && (
              <AIAvatar>
                <Text style={{ color: 'white', fontSize: 12, fontWeight: 600 }}>
                  AI
                </Text>
              </AIAvatar>
            )}
            <MessageBubble type={message.type}>
              <Text style={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                {message.content}
              </Text>
              {message.type === 'bot' &&
                message.options &&
                message.options.length > 0 && (
                  <div
                    style={{
                      marginTop: 12,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    {message.options.map(option => (
                      <Button
                        key={option}
                        onClick={() => sendMessage(option)}
                        disabled={isLoading}
                        style={{
                          textAlign: 'left',
                          whiteSpace: 'normal',
                          height: 'auto',
                          padding: '8px 12px',
                        }}
                      >
                        {option}
                      </Button>
                    ))}
                  </div>
                )}
              {message.type === 'bot' && message.chartConfig && (
                <div style={{ marginTop: 12 }}>
                  <ChartDisplay chartConfig={message.chartConfig} />
                </div>
              )}
              {message.type === 'bot' &&
                message.data &&
                !(
                  message.data.length === 1 &&
                  Object.keys(message.data[0]).length === 1
                ) && (
                  <div style={{ marginTop: 12 }}>
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownloadCsv(message.data!)}
                    >
                      Download CSV
                    </Button>
                  </div>
                )}
              {message.type === 'bot' && message.responseId && (
                <>
                  <FeedbackRow>
                    <Text style={{ fontSize: 12, color: '#8c8c8c' }}>
                      Was this helpful?
                    </Text>
                    <Button
                      type="text"
                      size="small"
                      aria-label="This answer was helpful"
                      title="This answer was helpful"
                      className={
                        ratings[message.responseId] === 'up' ? 'selected' : ''
                      }
                      icon={
                        ratings[message.responseId] === 'up' ? (
                          <LikeFilled />
                        ) : (
                          <LikeOutlined />
                        )
                      }
                      onClick={() => handleRate(message.responseId!, 'up')}
                    />
                    <Button
                      type="text"
                      size="small"
                      aria-label="This answer was not helpful"
                      title="This answer was not helpful"
                      className={
                        ratings[message.responseId] === 'down' ? 'selected' : ''
                      }
                      icon={
                        ratings[message.responseId] === 'down' ? (
                          <DislikeFilled />
                        ) : (
                          <DislikeOutlined />
                        )
                      }
                      onClick={() => handleRate(message.responseId!, 'down')}
                    />
                  </FeedbackRow>
                  {ratings[message.responseId] === 'down' &&
                    (commentSubmitted[message.responseId] ? (
                      <Text
                        style={{
                          marginTop: 8,
                          fontSize: 12,
                          color: '#8c8c8c',
                        }}
                      >
                        Thanks — sent
                      </Text>
                    ) : (
                      <CommentBox>
                        <Input.TextArea
                          value={commentDrafts[message.responseId] ?? ''}
                          onChange={e => {
                            const { responseId } = message;
                            const { value } = e.target;
                            if (!responseId) return;
                            setCommentDrafts(prev => ({
                              ...prev,
                              [responseId]: value,
                            }));
                          }}
                          placeholder="What was wrong? (optional)"
                          maxLength={FEEDBACK_COMMENT_MAX}
                          autoSize={{ minRows: 1, maxRows: 3 }}
                        />
                        <Button
                          type="primary"
                          size="small"
                          icon={<SendOutlined />}
                          disabled={
                            !(commentDrafts[message.responseId] ?? '').trim()
                          }
                          onClick={() =>
                            handleSubmitComment(message.responseId!)
                          }
                        />
                      </CommentBox>
                    ))}
                </>
              )}
            </MessageBubble>
          </MessageWrapper>
        ))}
        {isLoading && (
          <MessageWrapper type="bot">
            <AIAvatar>
              <Text style={{ color: 'white', fontSize: 12, fontWeight: 600 }}>
                AI
              </Text>
            </AIAvatar>
            <MessageBubble type="bot">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '4px',
                }}
              >
                <Spin size="small" />
                <Text style={{ marginLeft: '8px' }}>Thinking...</Text>
              </div>
            </MessageBubble>
          </MessageWrapper>
        )}
        <div ref={messagesEndRef} />
      </MessagesContainer>

      <InputContainer>
        <Input
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask a question about your data..."
          disabled={isLoading}
          size="large"
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={() => sendMessage()}
          disabled={!inputValue.trim() || isLoading}
          size="large"
        />
      </InputContainer>
    </ChatContainer>
  );
};
